import {inject, Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree} from '@angular/router';
import {Observable, of} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';
import {
    MSAL_GUARD_CONFIG,
    MsalBroadcastService,
    MsalGuard,
    MsalGuardConfiguration,
    MsalService
} from '@azure/msal-angular';
import {Location} from '@angular/common';
import {ShineUserInfoService} from '../shine-auth/shine-user-info.service';
import {ClientService} from '../client.service';
import {ClientStateService} from '../client-state.service';
import {classifyShellLoadFailure} from '../shell-load-failure.util';
import {
    isConnectWorkflowCanvasVisualFixtureUrl
} from '../../../components/connect/workflows/connect-workflow-canvas-visual-fixture.guard';

/**
 * Enhanced MSAL guard that checks both MSAL authentication and Shine Auth permissions.
 * This guard extends the MsalGuard to add permission checks based on HubSpot data.
 */
@Injectable({
    providedIn: 'root'
})
export class EnhancedMsalGuard extends MsalGuard {
    private shineUserInfoService = inject(ShineUserInfoService);
    private clientService = inject(ClientService);
    private clientStateService = inject(ClientStateService);


    /**
     * Initializes a new instance of the EnhancedMsalGuard class.
     * @param msalGuardConfig The MSAL guard configuration.
     * @param msalBroadcastService The MSAL broadcast service.
     * @param msalService The MSAL service.
     * @param location The Angular location service.
     * @param router The Angular router.
     * @param shineUserInfoService The Shine user information service.
     */
    constructor() {
        const msalGuardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG);
        const msalBroadcastService = inject(MsalBroadcastService);
        const msalService = inject(MsalService);
        const location = inject(Location);
        const router = inject(Router);

        super(msalGuardConfig, msalBroadcastService, msalService, location, router);
        this.routerInstance = router;
    }

    // Store router instance for use in our methods
    private routerInstance: Router;

    // Storage keys for storing the last URL and client
    private readonly LAST_URL_KEY = 'shine_last_url';
    private readonly LAST_PATH_KEY = 'shine_last_path';
    private readonly CLIENT_ID_COOKIE = 'clientId';

    /**
     * Gets a cookie value by name
     * @param name The name of the cookie to retrieve
     * @returns The cookie value or null if not found
     */
    private getCookie(name: string): string | null {
        const nameEQ = name + "=";
        const ca = document.cookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) === ' ') c = c.substring(1, c.length);
            if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
        }
        return null;
    }

    /**
     * Determines whether a route can be activated.
     * @param route The route to check.
     * @param state The router state.
     * @returns An observable that resolves to a boolean or UrlTree.
     */
    override canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<boolean | UrlTree> {
        if (this.clientStateService.getOidcCallbackDisposition() !== null) {
            return of(false);
        }

        if (this.isLocalConnectWorkflowCanvasVisualFixtureRoute(state.url)) {
            this.clientStateService.setClientLoading(false, 'route-loading');
            this.clientStateService.setBetaBackendUnavailable(false);
            this.clientStateService.setShellLoadFailure(null);
            return of(true);
        }

        // Store the current URL in session storage for restoration after refresh
        // Skip storing login and default routes
        const currentUrl = state.url;
        if (currentUrl && 
            !currentUrl.includes('/login') && 
            !currentUrl.includes('/auth') && 
            currentUrl !== '/' && 
            currentUrl !== '/dashboard') {
            console.debug(`Storing current URL in session storage: ${currentUrl}`);
            sessionStorage.setItem(this.LAST_URL_KEY, currentUrl);
        }

        // First check MSAL authentication
        return super.canActivate(route, state).pipe(
            switchMap(canActivate => {
                if (!canActivate) {
                    console.warn('MSAL authentication failed');
                    return of(canActivate);
                }

                // AppComponent owns callback completion. Do not let this independent MSAL path
                // restore routes or bootstrap client state if the callback changed while super resolved.
                if (this.clientStateService.getOidcCallbackDisposition() !== null) {
                    return of(false);
                }

                // If MSAL authentication passes, check user permissions from Shine Auth
                console.debug('MSAL authentication passed, checking Shine Auth permissions');

                // Check if there's a saved client ID in the cookie
                const savedClientId = this.getCookie(this.CLIENT_ID_COOKIE);
                if (savedClientId && (!this.clientService.client || this.clientService.client.id !== savedClientId)) {
                    console.debug(`Found saved client ID in cookie: ${savedClientId}, ensuring it's set in ClientService`);
                    // We'll handle this asynchronously in the next step
                    return of(savedClientId).pipe(
                        switchMap(clientId => {
                            // Ensure the client is set in the ClientService
                            return new Promise<void>(resolve => {
                                this.clientService.setClientAsync(clientId).then(() => resolve());
                            });
                        }),
                        switchMap(() => {
                            // Continue with permission checking
                            const requiredPermissions = route.data?.['requiredPermissions'] || [];
                            if (requiredPermissions.length === 0) {
                                console.debug('No required permissions specified, allowing access');

                                // Check if we need to restore a previous URL
                                if (currentUrl === '/' || currentUrl === '/dashboard') {
                                    // First check localStorage for a saved path (for page reloads)
                                    const lastPath = localStorage.getItem(this.LAST_PATH_KEY);
                                    if (lastPath && lastPath !== currentUrl) {
                                        console.debug(`Restoring previous path from localStorage: ${lastPath}`);
                                        // Clear the saved path to prevent future redirects
                                        localStorage.removeItem(this.LAST_PATH_KEY);
                                        // Return a UrlTree to redirect to the last path
                                        return of(this.routerInstance.parseUrl(lastPath));
                                    }

                                    // If no path in localStorage, check sessionStorage (for navigation)
                                    const lastUrl = sessionStorage.getItem(this.LAST_URL_KEY);
                                    if (lastUrl && lastUrl !== currentUrl) {
                                        console.debug(`Restoring previous URL from session storage: ${lastUrl}`);
                                        // Return a UrlTree to redirect to the last URL
                                        return of(this.routerInstance.parseUrl(lastUrl));
                                    }
                                }

                                return of(true);
                            }

                            // If permissions are required, continue with the normal flow
                            return this.checkPermissions(route, currentUrl);
                        })
                    );
                }

                // Get required permissions from route data
                const requiredPermissions = route.data?.['requiredPermissions'] || [];
                if (requiredPermissions.length === 0) {
                    console.debug('No required permissions specified, allowing access');

                    // Check if we need to restore a previous URL
                    if (currentUrl === '/' || currentUrl === '/dashboard') {
                        // First check localStorage for a saved path (for page reloads)
                        const lastPath = localStorage.getItem(this.LAST_PATH_KEY);
                        if (lastPath && lastPath !== currentUrl) {
                            console.debug(`Restoring previous path from localStorage: ${lastPath}`);
                            // Clear the saved path to prevent future redirects
                            localStorage.removeItem(this.LAST_PATH_KEY);
                            // Return a UrlTree to redirect to the last path
                            return of(this.routerInstance.parseUrl(lastPath));
                        }

                        // If no path in localStorage, check sessionStorage (for navigation)
                        const lastUrl = sessionStorage.getItem(this.LAST_URL_KEY);
                        if (lastUrl && lastUrl !== currentUrl) {
                            console.debug(`Restoring previous URL from session storage: ${lastUrl}`);
                            // Return a UrlTree to redirect to the last URL
                            return of(this.routerInstance.parseUrl(lastUrl));
                        }
                    }

                    return of(true);
                }

                // Check permissions using the extracted method
                return this.checkPermissions(route, currentUrl);
            })
        );
    }
    /**
     * Checks if the user has the required permissions for a route
     * @param route The route to check permissions for
     * @param currentUrl The current URL
     * @returns An observable that resolves to a boolean or UrlTree
     */
    private checkPermissions(route: ActivatedRouteSnapshot, currentUrl: string): Observable<boolean | UrlTree> {
        const requiredPermissions = route.data?.['requiredPermissions'] || [];

        // Check if user has required permissions
        return this.shineUserInfoService.getUserRights().pipe(
            map(response => {
                // Extract product names from all clients as permissions
                const userPermissions: string[] = [];

                if (response?.status === 'success' && response.data?.clients) {
                    // Collect all product names from all clients
                    response.data.clients.forEach(clientWithProducts => {
                        if (clientWithProducts.enabledProducts) {
                            clientWithProducts.enabledProducts.forEach(product => {
                                if (product.name) {
                                    userPermissions.push(product.name);
                                }
                            });
                        }
                    });
                }

                console.debug('Extracted user permissions:', userPermissions);

                // Check if all required permissions are present
                const hasPermissions = requiredPermissions.every((permission: string) => {
                    // First check exact match
                    if (userPermissions.includes(permission)) {
                        return true;
                    }

                    // If no exact match, check if any permission string contains the required permission
                    return userPermissions.some(userPerm =>
                        typeof userPerm === 'string' && userPerm.includes(permission)
                    );
                });

                if (hasPermissions) {
                    this.clientStateService.clearNonTokenShellLoadFailure();
                    console.debug('User has required permissions, allowing access');

                    // Check if we need to restore a previous URL
                    if (currentUrl === '/' || currentUrl === '/dashboard') {
                        // First check localStorage for a saved path
                        const lastPath = localStorage.getItem(this.LAST_PATH_KEY);
                        if (lastPath && lastPath !== currentUrl) {
                            console.debug(`Restoring previous path from localStorage: ${lastPath}`);
                            localStorage.removeItem(this.LAST_PATH_KEY);
                            return this.routerInstance.parseUrl(lastPath);
                        }

                        // Check sessionStorage
                        const lastUrl = sessionStorage.getItem(this.LAST_URL_KEY);
                        if (lastUrl && lastUrl !== currentUrl) {
                            console.debug(`Restoring previous URL from session storage: ${lastUrl}`);
                            return this.routerInstance.parseUrl(lastUrl);
                        }
                    }

                    return true;
                } else {
                    this.clientStateService.setBetaBackendUnavailable(false);
                    this.clientStateService.setShellLoadFailure('access-denied');
                    this.clientStateService.setClientLoading(false, 'rbac-tenant-denial');
                    console.warn(`User lacks required permissions: ${requiredPermissions.join(', ')}`);
                    console.debug('Available permissions:', userPermissions);
                    return false;
                }
            }),
            catchError(error => {
                this.clientStateService.setBetaBackendUnavailable(false);
                this.clientStateService.setShellLoadFailure(classifyShellLoadFailure(error));
                this.clientStateService.setClientLoading(false, 'backend-unavailable');
                return of(false);
            })
        ) as Observable<boolean | UrlTree>;
    }

    private isLocalConnectWorkflowCanvasVisualFixtureRoute(url: string): boolean {
        return isConnectWorkflowCanvasVisualFixtureUrl(url);
    }
}
